// @ts-nocheck
import {
  Bind,
  Box,
  ChevronLeftIcon,
  ChevronRightIcon,
  Grid,
  Heading,
  PauseIcon,
  PlayIcon,
  StackRow,
  Text,
  signal,
} from "@smoothie-framework/ui";
import { asset, Gutter } from "./shared";

const audioPreviews = [
  {
    title: "The Vloodmir Vampires",
    creator: "Victoria Jenkins",
    artwork: "velvet-echo.png",
    duration: 90.226939,
    source: "vloodmir-vampires.mp3",
  },
  {
    title: "Beauty and the Geek",
    creator: "Stephanie Williams",
    artwork: "amber-pulse.png",
    duration: 121.6,
    source: "beauty-and-the-geek.mp3",
  },
  {
    title: "When in Rome",
    creator: "Stephanie Williams",
    artwork: "jade-hush.png",
    duration: 30.197551,
    source: "when-in-rome.mp3",
  },
  {
    title: "Stawberries",
    creator: "Zorgie Sanchez",
    artwork: "cherry-current.png",
    duration: 98.403265,
    source: "strawberries.mp3",
  },
] as const;

const selectedAudio = signal(1);
const isAudioPlaying = signal(false);
const playbackSeconds = signal(0);
const playbackDurationSeconds = signal(audioPreviews[selectedAudio()].duration);
const audioPlayer = new window.Audio();

let playbackAnimationFrame: number | undefined;
let carouselEnterFrame: number | undefined;
let loadedAudioIndex: number | undefined;

function formatPlaybackTime(seconds: number) {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);

  return `${minutes}:${String(remainingSeconds).padStart(2, "0")}`;
}

function updateProgress(seconds: number) {
  const duration = playbackDurationSeconds();
  const clampedSeconds = Math.min(seconds, duration);
  playbackSeconds.set(clampedSeconds);

  const progressFill = document.querySelector<HTMLElement>(".progress-line span");
  progressFill?.style.setProperty("width", `${(clampedSeconds / duration) * 100}%`);
}

function updateSelectedPreviewState(isPlaying: boolean) {
  const selectedPreview = document.querySelector<HTMLButtonElement>(".audio-orb.slot-center");
  const preview = audioPreviews[selectedAudio()];

  selectedPreview?.classList.toggle("is-playing", isPlaying);
  selectedPreview?.setAttribute(
    "aria-label",
    `${isPlaying ? "Pause" : "Play"} ${preview.title}`,
  );
}

function stopAudioPlayback() {
  window.cancelAnimationFrame(playbackAnimationFrame);
  audioPlayer.pause();
  isAudioPlaying.set(false);
  updateSelectedPreviewState(false);
}

function trackPlayback() {
  updateProgress(audioPlayer.currentTime);

  if (!audioPlayer.paused && !audioPlayer.ended) {
    playbackAnimationFrame = window.requestAnimationFrame(trackPlayback);
  }
}

function loadSelectedAudio() {
  const index = selectedAudio();

  if (loadedAudioIndex === index) {
    return;
  }

  audioPlayer.src = asset(`audio/${audioPreviews[index].source}`);
  audioPlayer.load();
  loadedAudioIndex = index;
}

function toggleAudioPlayback() {
  if (isAudioPlaying()) {
    stopAudioPlayback();
    return;
  }

  loadSelectedAudio();
  void audioPlayer.play().then(
    () => {
      isAudioPlaying.set(true);
      updateSelectedPreviewState(true);
      playbackAnimationFrame = window.requestAnimationFrame(trackPlayback);
    },
    () => {
      isAudioPlaying.set(false);
      updateSelectedPreviewState(false);
    },
  );
}

audioPlayer.addEventListener("loadedmetadata", () => {
  if (Number.isFinite(audioPlayer.duration)) {
    playbackDurationSeconds.set(audioPlayer.duration);
  }
});

audioPlayer.addEventListener("ended", () => {
  window.cancelAnimationFrame(playbackAnimationFrame);
  isAudioPlaying.set(false);
  updateSelectedPreviewState(false);
});

function getAudioSlot(index: number): "left" | "center" | "right" | "hidden" {
  const distance = (index - selectedAudio() + audioPreviews.length) % audioPreviews.length;

  if (distance === 0) {
    return "center";
  }

  if (distance === 1) {
    return "right";
  }

  if (distance === audioPreviews.length - 1) {
    return "left";
  }

  return "hidden";
}

function selectAudio(index: number) {
  const previousIndex = selectedAudio();

  if (index === previousIndex) {
    return;
  }

  stopAudioPlayback();
  audioPlayer.currentTime = 0;
  updateProgress(0);
  playbackDurationSeconds.set(audioPreviews[index].duration);
  loadedAudioIndex = undefined;

  const goesBackward = index === (previousIndex + audioPreviews.length - 1) % audioPreviews.length;
  const enteringIndex = goesBackward
    ? (index + audioPreviews.length - 1) % audioPreviews.length
    : (index + 1) % audioPreviews.length;
  const enteringSlot = goesBackward ? "enter-left" : "enter-right";

  window.cancelAnimationFrame(carouselEnterFrame);
  selectedAudio.set(index);

  document.querySelectorAll<HTMLButtonElement>("[id^='audio-preview-']").forEach((preview) => {
    const previewIndex = Number(preview.id.replace("audio-preview-", ""));
    const slot = previewIndex === enteringIndex ? enteringSlot : getAudioSlot(previewIndex);
    preview.className = `audio-orb slot-${slot}`;
  });

  const enteringPreview = document.getElementById(`audio-preview-${enteringIndex}`);

  if (!enteringPreview) {
    return;
  }

  void enteringPreview.offsetWidth;
  carouselEnterFrame = window.requestAnimationFrame(() => {
    enteringPreview.className = `audio-orb slot-${getAudioSlot(enteringIndex)}`;
  });
}

function AudioPreview({ preview, index }: { preview: (typeof audioPreviews)[number]; index: number }) {
  return (
    <button
      className={`audio-orb slot-${getAudioSlot(index)}`}
      id={`audio-preview-${index}`}
      aria-label={`Play ${preview.title}`}
      onClick={() => {
        if (index === selectedAudio()) {
          toggleAudioPlayback();
          return;
        }

        selectAudio(index);
      }}
    >
      <span className="audio-orb-stage">
        <img aria-hidden="true" className="audio-orb-art" draggable="false" src={asset(`audio/${preview.artwork}`)} alt="" />
        <span aria-hidden="true" className="audio-play-affordance">
          <span className="audio-play-icon"><PlayIcon size="md" /></span>
          <span className="audio-pause-icon"><PauseIcon size="md" /></span>
        </span>
      </span>
      <Text as="strong" className="audio-preview-title" textStyle="h3">
        {preview.title}
      </Text>
      <Text as="small" className="audio-preview-author" color="subtle" textStyle="body-sm">
        {preview.creator}
      </Text>
    </button>
  );
}

export function Audio() {
  return (
    <Box as="section" className="audio section-dark" background="background" py={{ base: 16, md: 20 }}>
      <Gutter>
        <Grid alignItems={{ base: "stretch", md: "start" }} columns={{ base: 1, md: "1fr 1.2fr" }} gap={{ base: 8, md: 20 }}>
          <Heading level={2} textStyle="h1">
            Hear the drama
            <br />
            come to life.
          </Heading>
          <Box maxW={170}>
            <Text as="p" color="subtle" mt={{ base: 5, md: 0 }} textStyle="body">
              Explore four audio stories. Hear how voices, pacing and sound bring each world to life.
            </Text>
          </Box>
        </Grid>
      </Gutter>
      <Gutter>
        <Box className="audio-orbs" mt={{ base: 8, md: 12 }}>
          {audioPreviews.map((preview, index) => (
            <AudioPreview index={index} key={preview.source} preview={preview} />
          ))}
        </Box>
      </Gutter>
      <StackRow className="audio-controls" gap={6} justify="center" maxW={116} mx="auto" mt={{ base: 8, md: 12 }} w="full">
        <button className="round-control" type="button" aria-label="Previous audio" onClick={() => selectAudio((selectedAudio() + audioPreviews.length - 1) % audioPreviews.length)}>
          <ChevronLeftIcon size="sm" />
        </button>
        <Box className="progress" w={{ base: 56, md: 80 }}>
          <Box className="progress-line"><span /></Box>
          <StackRow justify="between">
            <Text as="span" textStyle="caption" color="subtle"><Bind>{() => `${formatPlaybackTime(playbackSeconds())} / ${formatPlaybackTime(playbackDurationSeconds())}`}</Bind></Text>
            <Text as="span" textStyle="caption" color="subtle"><Bind>{() => `${String(selectedAudio() + 1).padStart(2, "0")} / 04`}</Bind></Text>
          </StackRow>
        </Box>
        <button className="round-control" type="button" aria-label="Next audio" onClick={() => selectAudio((selectedAudio() + 1) % audioPreviews.length)}>
          <ChevronRightIcon size="sm" />
        </button>
      </StackRow>
    </Box>
  );
}
